/**
 * Platform Admin → Add-ons tab.
 *
 * Two sections:
 *   1. Default pricing card — global monthly + annual discount %. Annual
 *      price auto-computes (monthly × 12 × (1 − discount/100)). Any edit
 *      here rolls out to every workspace that hasn't yet purchased AND
 *      doesn't carry a per-workspace override. Active paying workspaces
 *      keep their locked ``active_price`` — the platform edit does NOT
 *      re-price them mid-term.
 *   2. Workspace overview table — for every active workspace: name,
 *      status, active price + cycle, purchase date, source (payment vs
 *      manual), whether pricing is default or custom. Row actions:
 *        - Toggle add-on ON/OFF (manual grant / revoke)
 *        - Set / clear per-workspace pricing override
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Briefcase, ArrowClockwise, Sparkle, Prohibit, PencilSimple, Trash,
} from "@phosphor-icons/react";


export default function PlatformAddonsTab() {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [monthly, setMonthly] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [description, setDescription] = useState("");
  const [overview, setOverview] = useState(null);
  const [overrideDialog, setOverrideDialog] = useState(null);  // {workspace}

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/addons/enterprise-workforce/pricing");
      setPricing(data);
      setMonthly(data.monthly_price || 0);
      setDiscount(data.annual_discount_pct || 0);
      setDescription(data.description || "");
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/addons/enterprise-workforce/workspaces");
      setOverview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); loadOverview(); }, [load, loadOverview]);

  // Live-computed annual price so the super admin sees the number update
  // as they type. Backend re-derives the same on save so this is UX only.
  const previewAnnual = Math.round(monthly * 12 * (1 - discount / 100) * 100) / 100;

  const savePricing = async () => {
    if (monthly < 0) return toast.error("Monthly price cannot be negative");
    if (discount < 0 || discount > 100) return toast.error("Discount must be 0–100%");
    setBusy(true);
    try {
      await api.put("/platform/addons/enterprise-workforce/pricing", {
        monthly_price: monthly,
        annual_discount_pct: discount,
        description,
      });
      toast.success("Default pricing updated. New workspaces will see these prices.");
      await load();
      await loadOverview();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggleWorkspace = async (w, activate) => {
    if (activate && !window.confirm(`Grant Enterprise Workforce Suite to "${w.name}" free of charge for 1 year?`)) return;
    if (!activate && !window.confirm(`Revoke Enterprise Workforce Suite for "${w.name}"? Users will lose access immediately.`)) return;
    try {
      await api.post(`/platform/addons/enterprise-workforce/workspaces/${w.org_id}/toggle`, {
        active: activate,
        billing_cycle: "annual",
      });
      toast.success(activate ? "Add-on granted" : "Add-on revoked");
      await loadOverview();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="platform-addons-tab">
      {/* ============ Default pricing ============ */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-primary/10 p-2"><Briefcase size={20} className="text-primary" weight="duotone" /></div>
            <div>
              <CardTitle>Enterprise Workforce Suite — default pricing</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Applies to every workspace that hasn&apos;t yet purchased.
                <b> Currently-active paying customers keep their locked price</b> until their
                next renewal — you can override per-workspace in the table below.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Monthly price (₹)</Label>
              <Input
                type="number" min={0} step="0.01"
                value={monthly}
                onChange={e => setMonthly(parseFloat(e.target.value || 0))}
                data-testid="pricing-monthly"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Annual discount (%)</Label>
              <Input
                type="number" min={0} max={100} step="0.1"
                value={discount}
                onChange={e => setDiscount(parseFloat(e.target.value || 0))}
                data-testid="pricing-discount"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Annual price (auto)</Label>
              <div className="h-10 rounded-md border border-dashed bg-muted/40 flex items-center px-3 text-sm" data-testid="pricing-annual-preview">
                <span className="font-display font-bold">₹{previewAnnual.toLocaleString("en-IN")}</span>
                <span className="text-[10px] text-muted-foreground ml-2">
                  = {monthly.toLocaleString("en-IN")} × 12 × ({100 - discount}%)
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Marketing description (shown to buyers)</Label>
            <textarea
              className="w-full min-h-[70px] px-3 py-2 rounded-md border border-input bg-background text-sm"
              value={description}
              onChange={e => setDescription(e.target.value)}
              data-testid="pricing-description"
            />
          </div>
          <div className="flex gap-2 pt-2 border-t">
            <Button onClick={savePricing} disabled={busy} data-testid="pricing-save">
              {busy ? "Saving…" : "Save default pricing"}
            </Button>
            <Button variant="outline" onClick={() => { load(); loadOverview(); }} className="gap-1">
              <ArrowClockwise size={13} /> Refresh
            </Button>
            {pricing?.updated_at && (
              <div className="ml-auto text-[11px] text-muted-foreground self-center">
                Last updated {new Date(pricing.updated_at).toLocaleString()}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ============ Workspace overview ============ */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-base">Workspace add-on status</CardTitle>
            <span className="text-xs text-muted-foreground">Which workspaces have the add-on and at what price.</span>
            <Button variant="ghost" size="sm" onClick={loadOverview} className="ml-auto gap-1 h-7"><ArrowClockwise size={12} /> Refresh</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Active price</TableHead>
                  <TableHead>Effective offer</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="w-40">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!overview && (
                  <TableRow><TableCell colSpan={6} className="text-center py-6 text-sm text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {overview?.workspaces?.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No workspaces.</TableCell></TableRow>
                )}
                {(overview?.workspaces || []).map(w => {
                  const isActive = w.status === "active";
                  return (
                    <TableRow key={w.org_id} data-testid={`ws-row-${w.slug}`}>
                      <TableCell>
                        <div className="font-medium">{w.name}</div>
                        <div className="text-[10px] text-muted-foreground">/t/{w.slug}</div>
                      </TableCell>
                      <TableCell>
                        {isActive ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1" data-testid={`ws-status-${w.slug}`}>
                            <Sparkle size={10} weight="fill" /> Enterprise
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200" data-testid={`ws-status-${w.slug}`}>Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {isActive ? (
                          <>
                            <div className="font-medium">₹{w.active_price?.toLocaleString("en-IN") || "—"}</div>
                            <div className="text-[10px] text-muted-foreground">{w.billing_cycle}</div>
                          </>
                        ) : <span className="text-muted-foreground italic">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {w.has_custom_pricing ? (
                          <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200 mb-1">Custom</Badge>
                        ) : (
                          <Badge className="bg-slate-50 text-slate-600 border-slate-200 mb-1">Default</Badge>
                        )}
                        <div className="text-[11px] text-muted-foreground">
                          ₹{w.effective_pricing?.monthly_price?.toLocaleString("en-IN")}/mo · ₹{w.effective_pricing?.annual_price?.toLocaleString("en-IN")}/yr
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {w.source === "payment" && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Paid</Badge>}
                        {w.source === "super_admin_manual" && <Badge className="bg-amber-50 text-amber-800 border-amber-200">Manual</Badge>}
                        {!w.source && <span className="text-muted-foreground">—</span>}
                        {w.purchased_at && (<div className="text-[10px] text-muted-foreground mt-0.5">{w.purchased_at.slice(0, 10)}</div>)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          <Button variant="ghost" size="sm" onClick={() => setOverrideDialog(w)} className="h-7 gap-1" data-testid={`ws-price-${w.slug}`}>
                            <PencilSimple size={11} /> Price
                          </Button>
                          {isActive ? (
                            <Button variant="ghost" size="sm" onClick={() => toggleWorkspace(w, false)} className="h-7 gap-1 text-rose-600" data-testid={`ws-revoke-${w.slug}`}>
                              <Prohibit size={11} /> Revoke
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm" onClick={() => toggleWorkspace(w, true)} className="h-7 gap-1 text-emerald-700" data-testid={`ws-grant-${w.slug}`}>
                              <Sparkle size={11} /> Grant
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <OverrideDialog
        workspace={overrideDialog}
        onClose={() => setOverrideDialog(null)}
        onSaved={loadOverview}
      />
    </div>
  );
}


function OverrideDialog({ workspace, onClose, onSaved }) {
  const [monthly, setMonthly] = useState("");
  const [discount, setDiscount] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (workspace) {
      setMonthly(workspace.effective_pricing?.monthly_price ?? "");
      setDiscount(workspace.effective_pricing?.annual_discount_pct ?? "");
    }
  }, [workspace]);

  const annualPreview = Math.round(
    (parseFloat(monthly || 0)) * 12 * (1 - (parseFloat(discount || 0)) / 100) * 100
  ) / 100;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(
        `/platform/addons/enterprise-workforce/workspaces/${workspace.org_id}/pricing`,
        {
          custom_monthly_price: parseFloat(monthly || 0),
          custom_annual_discount_pct: parseFloat(discount || 0),
        },
      );
      toast.success(`Custom price set for "${workspace.name}"`);
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Remove custom pricing for "${workspace.name}" and revert to platform default?`)) return;
    setSaving(true);
    try {
      await api.put(
        `/platform/addons/enterprise-workforce/workspaces/${workspace.org_id}/pricing`,
        { custom_monthly_price: null, custom_annual_discount_pct: null },
      );
      toast.success("Reverted to platform default");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!workspace) return null;
  return (
    <Dialog open={!!workspace} onOpenChange={onClose}>
      <DialogContent data-testid="override-dialog">
        <DialogHeader>
          <DialogTitle>Custom pricing · {workspace.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            This override replaces the platform default for this workspace only.
            Does not affect other workspaces or the workspace&apos;s current locked
            price if it&apos;s already active.
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Monthly ₹</Label>
              <Input type="number" min={0} step="0.01" value={monthly} onChange={e => setMonthly(e.target.value)} data-testid="override-monthly" />
            </div>
            <div>
              <Label>Discount %</Label>
              <Input type="number" min={0} max={100} step="0.1" value={discount} onChange={e => setDiscount(e.target.value)} data-testid="override-discount" />
            </div>
            <div>
              <Label>Annual (auto)</Label>
              <div className="h-10 rounded-md border border-dashed bg-muted/40 flex items-center px-3 text-sm font-medium">
                ₹{annualPreview.toLocaleString("en-IN")}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          {workspace.has_custom_pricing && (
            <Button variant="ghost" onClick={clear} disabled={saving} className="text-rose-600 mr-auto" data-testid="override-clear">
              <Trash size={13} className="mr-1" /> Remove override
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="override-save">
            {saving ? "Saving…" : "Save override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
